import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Heart, Shield } from 'lucide-react';

export const PomolvochnyeKolcaRiga = () => {
  return (
    <>
      <Helmet>
        <title>Помолвочные кольца в Риге | Кольца для предложения | Pure Atelier</title>
        <meta name="description" content="Помолвочные кольца в Риге с бриллиантами. Лучшие цены, сертифицированные бриллианты GIA/IGI. Бесплатная консультация. Pure Atelier Riga." />
        <meta name="keywords" content="помолвочные кольца Рига, кольцо для предложения, помолвочные кольца с бриллиантом, engagement rings Riga" />
        <link rel="canonical" href="https://pureatelier.lv/pomolvochnye-kolca-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/1616096/pexels-photo-1616096.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Помолвочные кольца в Риге
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Эксклюзивные помолвочные кольца с бриллиантами высочайшего качества. 
                Каждое кольцо — уникальное произведение ювелирного искусства.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть коллекцию <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20помолвочные%20кольца." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Консультация WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Сертифицированные", desc: "Бриллианты GIA и IGI" },
                { icon: Award, title: "Высокое качество", desc: "Ручная работа в Латвии" },
                { icon: Heart, title: "Индивидуальный дизайн", desc: "По вашим пожеланиям" },
                { icon: Shield, title: "Гарантия", desc: "Полная гарантия на изделия" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Почему выбирают помолвочные кольца Pure Atelier?
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier Riga</strong> предлагает эксклюзивные помолвочные кольца, созданные с любовью и мастерством. 
                  В нашей коллекции вы найдёте как классические кольца solitaire, так и современные дизайны с ореолом (halo).
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Наши преимущества</h3>
                <ul>
                  <li><strong>Лучшие цены в Риге</strong> — прямое сотрудничество с поставщиками бриллиантов</li>
                  <li><strong>Индивидуальный подход</strong> — поможем выбрать идеальное кольцо</li>
                  <li><strong>Быстрое изготовление</strong> — от 5 рабочих дней</li>
                  <li><strong>Бесплатная подгонка</strong> — изменение размера бесплатно</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Популярные стили помолвочных колец</h3>
                <p>
                  В нашей коллекции представлены различные стили — от классического <strong>Solitaire</strong> с одним бриллиантом до 
                  элегантных колец <strong>Halo</strong> с мелкими бриллиантами вокруг центрального камня. 
                  Также предлагаем кольца <strong>Bezel</strong> с более надёжным креплением бриллианта.
                </p>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=rings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Смотреть все кольца <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Готовы выбрать помолвочное кольцо?</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Свяжитесь с нами через WhatsApp для бесплатной консультации и помощи в выборе кольца.
            </p>
            <a href="https://wa.me/37128448991?text=Здравствуйте!%20Интересуют%20помолвочные%20кольца." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> Написать в WhatsApp
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default PomolvochnyeKolcaRiga;
